import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Icon, InfoIcon } from "lucide-react";
import { horseHead } from '@lucide/lab';
import { Upload, AlertTriangle, Camera, X, Plus, Edit, Trash2, Hammer, Bug, Syringe, Heart, Stethoscope, User, Utensils, FolderOpen, Folder } from 'lucide-react';

import { useNotification } from "../../contexts/NotificationContext";
import { useSubscription } from "../../hooks/useSubscription";
import { getApiUrl } from "../../config/api";

// Utility function to format date without timezone conversion
const formatDate = (dateString) => {
  if (!dateString) return 'No date';
  // Split the date string and format it directly to avoid timezone issues
  const [year, month, day] = dateString.split('T')[0].split('-');
  return `${month}/${day}/${year}`;
};

export default function Horses() {
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning } = useNotification();
  const { hasFeature } = useSubscription();
  const [horses, setHorses] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [newHorse, setNewHorse] = useState({
    name: "",
    breed: "",
    dob: "",
    sex: "",
    color: "",
    notes: "",
    age: "",
    weight: "",
    registration_number: "",
    microchip_number: "",
    horse_image: "",
    family_member_id: ""
  });
  const [filterText, setFilterText] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [showForm, setShowForm] = useState(false);

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingHorse, setDeletingHorse] = useState(null);


  // Expand/collapse state
  const [expandedHorses, setExpandedHorses] = useState(new Set());
  const [expandedCategories, setExpandedCategories] = useState(new Map());
  const [horseData, setHorseData] = useState(new Map()); // Store fetched data for each horse

  const [showCameraOptions, setShowCameraOptions] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    fetchHorses();
    if (hasFeature('family_plan')) {
      fetchFamilyMembers();
    }
    // Check if user is on mobile device
    setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    
    // Add click outside handler for camera options
    const handleClickOutside = (event) => {
      if (showCameraOptions && !event.target.closest('.camera-options-container')) {
        setShowCameraOptions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCameraOptions, hasFeature]);


  const fetchHorses = async () => {
    try {
      const res = await axios.get(getApiUrl("/api/horses"), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setHorses(res.data);
    } catch (error) {
      console.error("Error fetching horses:", error);
    }
  };

  const fetchFamilyMembers = async () => {
    try {
      const res = await axios.get(getApiUrl("/api/settings/family-members"), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setFamilyMembers(res.data);
    } catch (error) {
      console.error("Error fetching family members:", error);
    }
  };

  const addHorse = async () => {
    // Validate required fields
    if (!newHorse.name.trim()) {
      showWarning("Horse name is required");
      return;
    }

    // Prepare horse data with defaults for numeric fields
    const horseData = {
      ...newHorse,
      weight: newHorse.weight && newHorse.weight.trim() !== "" ? parseFloat(newHorse.weight) : null,
      age: newHorse.age && newHorse.age.trim() !== "" ? parseInt(newHorse.age) : null,
      // Ensure other fields are properly formatted
      name: newHorse.name.trim(),
      breed: newHorse.breed.trim() || null,
      color: newHorse.color.trim() || null,
      notes: newHorse.notes.trim() || null,
      registration_number: newHorse.registration_number.trim() || null,
      microchip_number: newHorse.microchip_number.trim() || null,
      dob: newHorse.dob || null,
      sex: newHorse.sex || null,
      horse_image: newHorse.horse_image || null,
      // Handle "self" selection - set to null for primary account
      family_member_id: newHorse.family_member_id === "self" ? null : (newHorse.family_member_id || null)
    };

    // If the user is not on a family plan, ensure we do not send a family_member_id
    try {
      // hasFeature is available from useSubscription at top of file
      if (typeof hasFeature === 'function' && !hasFeature('family_plan')) {
        horseData.family_member_id = null;
      }
    } catch (_) {
      horseData.family_member_id = null;
    }

    try {
      await axios.post(getApiUrl("/api/horses"), horseData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setNewHorse({
        name: "",
        breed: "",
        dob: "",
        sex: "",
        color: "",
        notes: "",
        age: "",
        weight: "",
        registration_number: "",
        microchip_number: "",
        horse_image: "",
        family_member_id: ""
      });
      showSuccess("Horse added successfully!");
      fetchHorses();
    } catch (err) {
      console.error("Add error:", err);
      showError("Failed to add horse");
    }
  };

  const deleteHorse = async (id) => {
    try {
      await axios.delete(getApiUrl(`/api/horses/${id}`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      showSuccess("Horse deleted successfully!");
      fetchHorses();
    } catch (err) {
      console.error("Delete error:", err);
      showError("Failed to delete horse");
    }
  };

  const confirmDelete = (horse) => {
    setDeletingHorse(horse);
    setShowDeleteConfirm(true);
  };


  const toggleHorseExpansion = (horseId) => {
    const newExpandedHorses = new Set(expandedHorses);
    if (newExpandedHorses.has(horseId)) {
      newExpandedHorses.delete(horseId);
    } else {
      newExpandedHorses.add(horseId);
    }
    setExpandedHorses(newExpandedHorses);
  };

  const toggleCategoryExpansion = (horseId, category) => {
    const key = `${horseId}-${category}`;
    const newExpandedCategories = new Map(expandedCategories);
    if (newExpandedCategories.has(key)) {
      newExpandedCategories.delete(key);
    } else {
      newExpandedCategories.set(key, true);
      // Fetch data for this category if not already loaded
      fetchCategoryData(horseId, category);
    }
    setExpandedCategories(newExpandedCategories);
  };

  const fetchCategoryData = async (horseId, category) => {
    try {
      let endpoint = '';
      switch (category) {
        case 'farrier':
          endpoint = `/api/horses/${horseId}/farrier`;
          break;
        case 'dentalcare':
          endpoint = `/api/horses/${horseId}/dentalcare`;
          break;
        case 'deworming':
          endpoint = `/api/horses/${horseId}/deworming`;
          break;
        case 'vaccinations':
          endpoint = `/api/horses/${horseId}/vaccinations`;
          break;
        case 'breedings':
          endpoint = `/api/horses/${horseId}/breedings`;
          break;
        case 'medicalhistory':
          endpoint = `/api/horses/${horseId}/medicalhistory`;
          break;
        default:
          return;
      }

      const res = await axios.get(getApiUrl(endpoint), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setHorseData(prev => {
        const newData = new Map(prev);
        if (!newData.has(horseId)) {
          newData.set(horseId, {});
        }
        const horseData = newData.get(horseId);
        horseData[category] = res.data;
        newData.set(horseId, { ...horseData });
        return newData;
      });
    } catch (error) {
      console.error(`Error fetching ${category} data:`, error);
      showError(`Failed to load ${category} data`);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      processImageFile(file);
    }
  };

  const processImageFile = (file) => {
    // Check file type
    if (!file.type.startsWith('image/')) {
      showWarning("Please select an image file");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        // Dynamic sizing based on original dimensions
        let targetSize = 200; // Default size
        let quality = 0.8; // Default quality
        
        // If image is very large, increase target size but maintain aspect ratio
        if (img.width > 1000 || img.height > 1000) {
          targetSize = 400; // Larger size for high-res images
        } else if (img.width > 500 || img.height > 500) {
          targetSize = 300; // Medium size for medium-res images
        }
        
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set canvas size to target size (square)
        canvas.width = targetSize;
        canvas.height = targetSize;
        
        // Calculate dimensions to maintain aspect ratio
        const size = Math.min(img.width, img.height);
        const startX = (img.width - size) / 2;
        const startY = (img.height - size) / 2;
        
        // Draw cropped and resized image
        ctx.drawImage(img, startX, startY, size, size, 0, 0, targetSize, targetSize);
        
        // Dynamic compression based on target size
        if (targetSize === 400) {
          quality = 0.7; // Lower quality for larger images
        } else if (targetSize === 300) {
          quality = 0.75; // Medium quality for medium images
        } else {
          quality = 0.8; // Higher quality for smaller images
        }
        
        // Convert to base64 with dynamic compression
        let resizedImage = canvas.toDataURL('image/jpeg', quality);
        
        // Check if the result is still too large and compress further if needed
        if (resizedImage.length > 2 * 1024 * 1024) {
          // If still too large, reduce quality further
          quality = Math.max(0.3, quality - 0.2);
          resizedImage = canvas.toDataURL('image/jpeg', quality);
          
          // If still too large, reduce size
          if (resizedImage.length > 2 * 1024 * 1024) {
            targetSize = Math.floor(targetSize * 0.8);
            canvas.width = targetSize;
            canvas.height = targetSize;
            ctx.drawImage(img, startX, startY, size, size, 0, 0, targetSize, targetSize);
            resizedImage = canvas.toDataURL('image/jpeg', 0.6);
          }
        }
        
        setNewHorse({ ...newHorse, horse_image: resizedImage });
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleCameraCapture = () => {
    setShowCameraOptions(false);
    
    // Create a file input with camera capture attribute
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment'; // Use back camera by default
    
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        processImageFile(file);
      }
    };
    
    input.click();
  };

  const handleFrontCameraCapture = () => {
    setShowCameraOptions(false);
    
    // Create a file input with front camera capture
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'user'; // Use front camera
    
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        processImageFile(file);
      }
    };
    
    input.click();
  };

  const removeNewHorseImage = () => {
    setNewHorse({ ...newHorse, horse_image: "" });
  };


  return (
    <div className="w-full min-h-screen bg-gray-50">
      <div className="p-1 sm:p-6 max-w-full overflow-x-hidden">
      
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center">
          <Icon iconNode={horseHead} className="mr-2 h-8 w-8 text-black-600" /> Horses
        </h2>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/horses/feedplan')}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            <Utensils className="w-4 h-4" />
            <span className="hidden sm:inline">Feed Plans</span>
          </button>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add</span>
          </button>
        </div>
      </div>
      

      {/* List Section */}
      <div className="bg-white shadow-md rounded-md px-1 py-1 sm:p-6 border border-gray-200">

        <div className="space-y-3 mb-6">
          {/* Filter Row */}
          <div className="w-full">
            <input
                type="text"
                placeholder="Filter by name or breed only"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 w-full"
              />     
          </div>
          
          {/* Sort Row */}
          <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Sort by:</label>
              <select
                value={sortField}
                onChange={(e) => setSortField(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 flex-1 sm:flex-none"
              >
                <option value="name">Name</option>
                <option value="age">Age</option>
                <option value="breed">Breed</option>
              </select>

              <button
                onClick={() => setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))}
              className="text-sm text-blue-600 hover:underline px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
              >
                {sortDirection === "asc" ? "↑ Asc" : "↓ Desc"}
              </button>
          </div>
        </div>

        {horses.length === 0 ? (
          <p className="text-gray-500">No horses added yet.</p>
        ) : (
          <div className="space-y-6">
            {horses
  .filter((h) =>
    h.name.toLowerCase().includes(filterText.toLowerCase()) ||
    h.breed.toLowerCase().includes(filterText.toLowerCase())
  )
  .sort((a, b) => {
    if (sortField === "age") {
      return sortDirection === "asc" ? a.age - b.age : b.age - a.age;
    } else if (sortField === "breed") {
      return sortDirection === "asc" ? a.breed.localeCompare(b.breed) : b.breed.localeCompare(a.breed);
    } else {
      return sortDirection === "asc"
        ? a[sortField].localeCompare(b[sortField])
        : b[sortField].localeCompare(a[sortField]);
    }
  })
  .map((h) => (
              <div
                key={h.id}
                className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                {/* Horse Header Section */}
                <div className="p-1 sm:p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                  <div className="flex items-center sm:items-start sm:justify-between gap-4">
                    {/* Expand/Collapse Button - Left side, centered */}
                    <div className="flex-shrink-0 self-center">
                      <button
                        onClick={() => toggleHorseExpansion(h.id)}
                        className="text-gray-500 hover:text-gray-700 transition-colors p-2"
                        title={expandedHorses.has(h.id) ? "Collapse" : "Expand"}
                      >
                        {expandedHorses.has(h.id) ? (
                          <FolderOpen className="w-8 h-8" />
                        ) : (
                          <Folder className="w-8 h-8" />
                        )}
                      </button>
                    </div>
                    
                    <div className="flex-1 flex flex-col sm:flex-row sm:items-start gap-4">
                      {/* Horse Image */}
                      <div className="flex-shrink-0 self-center sm:self-start">
                        {h.horse_image ? (
                          <img 
                            src={h.horse_image} 
                            alt={h.name} 
                            className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-lg border-2 border-gray-300 shadow-sm"
                          />
                        ) : (
                          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-200 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                            <span className="text-gray-400 text-xs text-center">No photo</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Horse Info */}
                      <div className="flex-1 text-center sm:text-left">
                        <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
                          <button
                            onClick={() => toggleHorseExpansion(h.id)}
                            className="text-blue-700 hover:text-blue-800 transition-colors cursor-pointer"
                          >
                            {h.name}
                          </button>
                        </h3>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-gray-600">
                          {h.sex && (
                            <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-blue-100 text-blue-800 self-center sm:self-start">
                              {h.sex}
                            </span>
                          )}
                          {h.breed && (
                            <span className="hidden sm:inline text-gray-700">
                              <span className="font-medium">Breed:</span> {h.breed}
                            </span>
                          )}
                          {h.age && h.age !== 0 && h.age !== null && (
                            <span className="hidden sm:inline text-gray-700">
                              <span className="font-medium">Age:</span> {h.age} years
                            </span>
                          )}
                          {h.color && (
                            <span className="hidden sm:inline text-gray-700">
                              <span className="font-medium">Color:</span> {h.color}
                            </span>
                          )}
                        </div>
                        {/* Show family member assignment */}
                        <div className="mt-2 text-center sm:text-left">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            {h.family_member_name ? 
                              `${h.family_member_name} ${h.family_member_relationship ? `(${h.family_member_relationship})` : ''}` : 
                              h.owner_name || 'Primary Account'
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Delete Button - Right side */}
                    <div className="flex-shrink-0 self-center">
                    <button
                      onClick={() => confirmDelete(h)}
                        className="text-red-600 hover:text-red-800 font-semibold text-sm px-3 py-2 hover:bg-red-50 rounded-md transition-colors"
                        title="Delete horse"
                    >
                        <span className="sm:hidden">
                          <Trash2 className="w-4 h-4" />
                        </span>
                        <span className="hidden sm:inline">Delete</span>
                    </button>
                  </div>
                </div>

                  {/* Horse Details - Moved into shaded area */}
                  <div className="hidden sm:block mt-4 pt-4 border-t border-blue-100">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 text-sm text-gray-600">
                    {h.weight && h.weight !== 0 && h.weight !== null && (
                      <div className="text-center sm:text-left">
                        <span className="font-medium">Weight:</span> {h.weight} lbs
                      </div>
                    )}
                    {h.registration_number && (
                      <div className="text-center sm:text-left">
                        <span className="font-medium">Reg #:</span> {h.registration_number}
                      </div>
                    )}
                    {h.microchip_number && (
                      <div className="text-center sm:text-left">
                        <span className="font-medium">Chip:</span> {h.microchip_number}
                      </div>
                    )}
                    {h.dob && (
                      <div className="text-center sm:text-left">
                        <span className="font-medium">DOB:</span> {formatDate(h.dob)}
                      </div>
                    )}
                  </div>
                  
                  {h.notes && (
                      <div className="mt-3 p-3 bg-white bg-opacity-50 rounded-md">
                        <span className="text-sm text-gray-600 italic">
                        <span className="font-medium">Notes:</span> {h.notes}
                      </span>
                    </div>
                  )}
                  </div>
                </div>

                {/* Expanded Content Section */}
                {expandedHorses.has(h.id) && (
                  <div className="border-t border-gray-100 bg-gray-50">
                    <div className="p-4 sm:p-6">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4">Horse Management</h4>
                      
                      {/* Categories Grid */}
                      <div className="space-y-4">
                        {/* Info/Edit Category */}
                        <div className="bg-white rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between p-4 border-b border-gray-100">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => toggleCategoryExpansion(h.id, 'info')}
                                className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors p-1"
                              >
                                {expandedCategories.has(`${h.id}-info`) ? (
                                  <FolderOpen className="w-5 h-5" />
                                ) : (
                                  <Folder className="w-5 h-5" />
                                )}
                                <InfoIcon className="w-4 h-4" />
                                <span className="font-medium">Info</span>
                              </button>
                            </div>
                      <button 
                        onClick={() => navigate(`/horses/${h.id}/info`)} 
                              className="flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors text-sm"
                      >
                              <Edit className="w-4 h-4" />
                              <span className="hidden sm:inline">Edit</span>
                      </button>
                          </div>
                          {expandedCategories.has(`${h.id}-info`) && (
                            <div className="p-4">
                              <p className="text-sm text-gray-600">Click "Edit" to manage horse information, photos, and basic details.</p>
                            </div>
                          )}
                        </div>

                        {/* Farrier Category */}
                        <div className="bg-white rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between p-4 border-b border-gray-100">
                            <div className="flex items-center gap-2">
                      <button 
                                onClick={() => toggleCategoryExpansion(h.id, 'farrier')}
                                className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors p-1"
                              >
                                {expandedCategories.has(`${h.id}-farrier`) ? (
                                  <FolderOpen className="w-5 h-5" />
                                ) : (
                                  <Folder className="w-5 h-5" />
                                )}
                                <Hammer className="w-4 h-4" />
                        <span className="font-medium">Farrier</span>
                      </button>
                            </div>
                            <button
                              onClick={() => navigate(`/horses/${h.id}/farrier`)}
                              className="flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 rounded-md hover:bg-green-100 transition-colors text-sm"
                            >
                              <Plus className="w-4 h-4" />
                              <span className="hidden sm:inline">Add</span>
                            </button>
                          </div>
                          {expandedCategories.has(`${h.id}-farrier`) && (
                            <div className="p-4">
                              {horseData.get(h.id)?.farrier?.length > 0 ? (
                                <div className="space-y-2">
                                  {horseData.get(h.id).farrier.map((record, index) => (
                                    <div key={index} className="flex items-start justify-between p-3 bg-gray-50 rounded-md gap-2">
                                      <div className="text-sm flex-1 min-w-0">
                                        <div className="font-medium">Date: {record.service_date ? formatDate(record.service_date) : 'No date'}</div>
                                        {record.services_performed && <div className="text-gray-600 truncate">- {record.services_performed}</div>}
                                        {record.notes && <div className="text-gray-600 truncate">- {record.notes}</div>}
                                      </div>
                                      <div className="flex gap-1">
                                        <button
                                          onClick={() => navigate(`/horses/${h.id}/farrier?edit=${record.id}`)}
                                          className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                                        >
                                          <Edit className="w-4 h-4" />
                                        </button>
                                        <button
                                          onClick={() => {/* Add delete functionality */}}
                                          className="p-1 text-red-600 hover:text-red-800 transition-colors"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-600">No farrier records found. Click "Add" to create the first record.</p>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Dental Category */}
                        <div className="bg-white rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between p-4 border-b border-gray-100">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => toggleCategoryExpansion(h.id, 'dentalcare')}
                                className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors p-1"
                              >
                                {expandedCategories.has(`${h.id}-dentalcare`) ? (
                                  <FolderOpen className="w-5 h-5" />
                                ) : (
                                  <Folder className="w-5 h-5" />
                                )}
                                <Stethoscope className="w-4 h-4" />
                                <span className="font-medium">Dental Care</span>
                              </button>
                            </div>
                      <button 
                        onClick={() => navigate(`/horses/${h.id}/dentalcare`)} 
                              className="flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-700 rounded-md hover:bg-purple-100 transition-colors text-sm"
                      >
                              <Plus className="w-4 h-4" />
                              <span className="hidden sm:inline">Add</span>
                      </button>
                          </div>
                          {expandedCategories.has(`${h.id}-dentalcare`) && (
                            <div className="p-4">
                              {horseData.get(h.id)?.dentalcare?.length > 0 ? (
                                <div className="space-y-2">
                                  {horseData.get(h.id).dentalcare.map((record, index) => (
                                    <div key={index} className="flex items-start justify-between p-3 bg-gray-50 rounded-md gap-2">
                                      <div className="text-sm flex-1 min-w-0">
                                        <div className="font-medium">Date: {record.exam_date ? formatDate(record.exam_date) : 'No date'}</div>
                                        {record.findings && <div className="text-gray-600 truncate">- {record.findings}</div>}
                                      </div>
                                      <div className="flex gap-1">
                      <button 
                                          onClick={() => navigate(`/horses/${h.id}/dentalcare?edit=${record.id}`)}
                                          className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                                        >
                                          <Edit className="w-4 h-4" />
                                        </button>
                                        <button
                                          onClick={() => {/* Add delete functionality */}}
                                          className="p-1 text-red-600 hover:text-red-800 transition-colors"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-600">No dental care records found. Click "Add" to create the first record.</p>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Wormings Category */}
                        <div className="bg-white rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between p-4 border-b border-gray-100">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => toggleCategoryExpansion(h.id, 'deworming')}
                                className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors p-1"
                              >
                                {expandedCategories.has(`${h.id}-deworming`) ? (
                                  <FolderOpen className="w-5 h-5" />
                                ) : (
                                  <Folder className="w-5 h-5" />
                                )}
                                <Bug className="w-4 h-4" />
                        <span className="font-medium">Wormings</span>
                      </button>
                            </div>
                      <button 
                              onClick={() => navigate(`/horses/${h.id}/deworming`)}
                              className="flex items-center gap-1 px-3 py-1 bg-orange-50 text-orange-700 rounded-md hover:bg-orange-100 transition-colors text-sm"
                            >
                              <Plus className="w-4 h-4" />
                              <span className="hidden sm:inline">Add</span>
                            </button>
                          </div>
                          {expandedCategories.has(`${h.id}-deworming`) && (
                            <div className="p-4">
                              {horseData.get(h.id)?.deworming?.length > 0 ? (
                                <div className="space-y-2">
                                  {horseData.get(h.id).deworming.map((record, index) => (
                                    <div key={index} className="flex items-start justify-between p-3 bg-gray-50 rounded-md gap-2">
                                      <div className="text-sm flex-1 min-w-0">
                                        <div className="font-medium">Date: {record.administered_date ? formatDate(record.administered_date) : 'No date'}</div>
                                        {record.product_used && <div className="text-gray-600 truncate">- {record.product_used}</div>}
                                        {record.notes && <div className="text-gray-600 truncate">- {record.notes}</div>}
                                      </div>
                                      <div className="flex gap-1">
                                        <button
                                          onClick={() => navigate(`/horses/${h.id}/deworming?edit=${record.id}`)}
                                          className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                                        >
                                          <Edit className="w-4 h-4" />
                                        </button>
                                        <button
                                          onClick={() => {/* Add delete functionality */}}
                                          className="p-1 text-red-600 hover:text-red-800 transition-colors"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-600">No deworming records found. Click "Add" to create the first record.</p>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Vaccinations Category */}
                        <div className="bg-white rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between p-4 border-b border-gray-100">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => toggleCategoryExpansion(h.id, 'vaccinations')}
                                className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors p-1"
                              >
                                {expandedCategories.has(`${h.id}-vaccinations`) ? (
                                  <FolderOpen className="w-5 h-5" />
                                ) : (
                                  <Folder className="w-5 h-5" />
                                )}
                                <Syringe className="w-4 h-4" />
                        <span className="font-medium">Vaccinations</span>
                      </button>
                            </div>
                      <button 
                              onClick={() => navigate(`/horses/${h.id}/vaccinations`)}
                              className="flex items-center gap-1 px-3 py-1 bg-yellow-50 text-yellow-700 rounded-md hover:bg-yellow-100 transition-colors text-sm"
                            >
                              <Plus className="w-4 h-4" />
                              <span className="hidden sm:inline">Add</span>
                            </button>
                          </div>
                          {expandedCategories.has(`${h.id}-vaccinations`) && (
                            <div className="p-4">
                              {horseData.get(h.id)?.vaccinations?.length > 0 ? (
                                <div className="space-y-2">
                                  {horseData.get(h.id).vaccinations.map((record, index) => (
                                    <div key={index} className="flex items-start justify-between p-3 bg-gray-50 rounded-md gap-2">
                                      <div className="text-sm flex-1 min-w-0">
                                        <div className="font-medium">Date: {record.administered_date ? formatDate(record.administered_date) : 'No date'}</div>
                                        {record.vaccine_type && <div className="text-gray-600 truncate">- {record.vaccine_type}</div>}
                                        {record.notes && <div className="text-gray-600 truncate">- {record.notes}</div>}
                                      </div>
                                      <div className="flex gap-1">
                                        <button
                                          onClick={() => navigate(`/horses/${h.id}/vaccinations?edit=${record.id}`)}
                                          className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                                        >
                                          <Edit className="w-4 h-4" />
                                        </button>
                                        <button
                                          onClick={() => {/* Add delete functionality */}}
                                          className="p-1 text-red-600 hover:text-red-800 transition-colors"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-600">No vaccination records found. Click "Add" to create the first record.</p>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Breedings Category */}
                        <div className="bg-white rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between p-4 border-b border-gray-100">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => toggleCategoryExpansion(h.id, 'breedings')}
                                className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors p-1"
                              >
                                {expandedCategories.has(`${h.id}-breedings`) ? (
                                  <FolderOpen className="w-5 h-5" />
                                ) : (
                                  <Folder className="w-5 h-5" />
                                )}
                                <Heart className="w-4 h-4" />
                        <span className="font-medium">Breedings</span>
                      </button>
                            </div>
                            <button
                              onClick={() => navigate(`/horses/${h.id}/breedings`)}
                              className="flex items-center gap-1 px-3 py-1 bg-pink-50 text-pink-700 rounded-md hover:bg-pink-100 transition-colors text-sm"
                            >
                              <Plus className="w-4 h-4" />
                              <span className="hidden sm:inline">Add</span>
                            </button>
                          </div>
                          {expandedCategories.has(`${h.id}-breedings`) && (
                            <div className="p-4">
                              {horseData.get(h.id)?.breedings?.length > 0 ? (
                                <div className="space-y-2">
                                  {horseData.get(h.id).breedings.map((record, index) => (
                                    <div key={index} className="flex items-start justify-between p-3 bg-gray-50 rounded-md gap-2">
                                      <div className="text-sm flex-1 min-w-0">
                                        <div className="font-medium">Date: {record.cycle_date ? formatDate(record.cycle_date) : 'No date'}</div>
                                        {record.notes && <div className="text-gray-600 truncate">- {record.notes}</div>}
                                      </div>
                                      <div className="flex gap-1">
                                        <button
                                          onClick={() => navigate(`/horses/${h.id}/breedings?edit=${record.id}`)}
                                          className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                                        >
                                          <Edit className="w-4 h-4" />
                                        </button>
                                        <button
                                          onClick={() => {/* Add delete functionality */}}
                                          className="p-1 text-red-600 hover:text-red-800 transition-colors"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-600">No breeding records found. Click "Add" to create the first record.</p>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Medical History Category */}
                        <div className="bg-white rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between p-4 border-b border-gray-100">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => toggleCategoryExpansion(h.id, 'medicalhistory')}
                                className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors p-1"
                              >
                                {expandedCategories.has(`${h.id}-medicalhistory`) ? (
                                  <FolderOpen className="w-5 h-5" />
                                ) : (
                                  <Folder className="w-5 h-5" />
                                )}
                                <Stethoscope className="w-4 h-4" />
                                <span className="font-medium">Medical History</span>
                              </button>
                            </div>
                      <button 
                        onClick={() => navigate(`/horses/${h.id}/medicalhistory`)} 
                              className="flex items-center gap-1 px-3 py-1 bg-red-50 text-red-700 rounded-md hover:bg-red-100 transition-colors text-sm"
                      >
                              <Plus className="w-4 h-4" />
                              <span className="hidden sm:inline">Add</span>
                      </button>
                    </div>
                          {expandedCategories.has(`${h.id}-medicalhistory`) && (
                            <div className="p-4">
                              {horseData.get(h.id)?.medicalhistory?.length > 0 ? (
                                <div className="space-y-2">
                                  {horseData.get(h.id).medicalhistory.map((record, index) => (
                                    <div key={index} className="flex items-start justify-between p-3 bg-gray-50 rounded-md gap-2">
                                      <div className="text-sm flex-1 min-w-0">
                                        <div className="font-medium">Date: {record.treatment_date ? formatDate(record.treatment_date) : 'No date'}</div>
                                        {record.notes && <div className="text-gray-600 truncate">- {record.notes}</div>}
                  </div>
                                      <div className="flex gap-1">
                                        <button
                                          onClick={() => navigate(`/horses/${h.id}/medicalhistory?edit=${record.id}`)}
                                          className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                                        >
                                          <Edit className="w-4 h-4" />
                                        </button>
                                        <button
                                          onClick={() => {/* Add delete functionality */}}
                                          className="p-1 text-red-600 hover:text-red-800 transition-colors"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-600">No medical history records found. Click "Add" to create the first record.</p>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Feed Plan Category */}
                        <div className="bg-white rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between p-4 border-b border-gray-100">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => toggleCategoryExpansion(h.id, 'feedplan')}
                                className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors p-1"
                              >
                                {expandedCategories.has(`${h.id}-feedplan`) ? (
                                  <FolderOpen className="w-5 h-5" />
                                ) : (
                                  <Folder className="w-5 h-5" />
                                )}
                                <Utensils className="w-4 h-4" />
                                <span className="font-medium">Feed Plan</span>
                              </button>
                            </div>
                            <button
                              onClick={() => navigate(`/horses/${h.id}/feedplan`)}
                              className="flex items-center gap-1 px-3 py-1 bg-emerald-50 text-emerald-700 rounded-md hover:bg-emerald-100 transition-colors text-sm"
                            >
                              <Plus className="w-4 h-4" />
                              <span className="hidden sm:inline">Open</span>
                            </button>
                          </div>
                          {expandedCategories.has(`${h.id}-feedplan`) && (
                            <div className="p-4">
                              <p className="text-sm text-gray-600">Manage feeding plans for this horse. Click "Open" to build or edit a plan.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Horse Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70]">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Add a New Horse</h2>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Horse Image Upload */}
              <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <label className="block text-sm font-medium text-gray-700 mb-3">Horse Photo</label>
                <div className="flex items-center gap-4">
                  {newHorse.horse_image ? (
                    <div className="relative">
                      <img 
                        src={newHorse.horse_image} 
                        alt="Horse preview" 
                        className="w-24 h-24 object-cover rounded-lg border-2 border-gray-300"
                      />
                      <button
                        onClick={removeNewHorseImage}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-24 h-24 bg-gray-200 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                      <span className="text-gray-400 text-xs text-center">No image</span>
                    </div>
                  )}
                  <div className="flex flex-col gap-2">
                    {/* Mobile Camera Options */}
                    {isMobile && (
                      <div className="flex gap-2 camera-options-container">
                        <button
                          onClick={() => setShowCameraOptions(!showCameraOptions)}
                          className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                        >
                          📷 Camera
                        </button>
                        {showCameraOptions && (
                          <div className="absolute z-10 mt-10 bg-white border border-gray-200 rounded-md shadow-lg">
                            <button
                              onClick={() => {
                                // Add camera functionality
                                setShowCameraOptions(false);
                              }}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 rounded flex items-center gap-2"
                            >
                              📷 Rear Camera
                            </button>
                            <button
                              onClick={() => {
                                // Add front camera functionality
                                setShowCameraOptions(false);
                              }}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 rounded flex items-center gap-2"
                            >
                              🤳 Front Camera
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* File Upload */}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="horse-image-upload-modal"
                    />
                    <label
                      htmlFor="horse-image-upload-modal"
                      className="px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors text-sm cursor-pointer text-center"
                    >
                      {newHorse.horse_image ? 'Change Photo' : 'Upload Photo'}
                    </label>
                    <p className="text-xs text-gray-500">Automatically optimized to fit database requirements</p>
                    {isMobile && (
                      <p className="text-xs text-green-600">📱 Use Camera button for mobile photos</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    placeholder="Horse name"
                    value={newHorse.name}
                    onChange={(e) => setNewHorse({ ...newHorse, name: e.target.value })}
                    className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Breed</label>
                  <input
                    type="text"
                    placeholder="Horse breed"
                    value={newHorse.breed}
                    onChange={(e) => setNewHorse({ ...newHorse, breed: e.target.value })}
                    className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                  <input
                    type="date"
                    value={newHorse.dob}
                    onChange={(e) => setNewHorse({ ...newHorse, dob: e.target.value })}
                    className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sex</label>
                  <select
                    value={newHorse.sex}
                    onChange={(e) => setNewHorse({ ...newHorse, sex: e.target.value })}
                    className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                  >
                    <option value="">Select sex</option>
                    <option value="Stallion">Stallion</option>
                    <option value="Mare">Mare</option>
                    <option value="Gelding">Gelding</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                  <input
                    type="text"
                    placeholder="Horse color"
                    value={newHorse.color}
                    onChange={(e) => setNewHorse({ ...newHorse, color: e.target.value })}
                    className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Weight (lbs)</label>
                  <input
                    type="number"
                    placeholder="Weight in pounds"
                    value={newHorse.weight}
                    onChange={(e) => setNewHorse({ ...newHorse, weight: e.target.value })}
                    className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label>
                  <input
                    type="text"
                    placeholder="Registration number"
                    value={newHorse.registration_number}
                    onChange={(e) => setNewHorse({ ...newHorse, registration_number: e.target.value })}
                    className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Microchip Number</label>
                  <input
                    type="text"
                    placeholder="Microchip number"
                    value={newHorse.microchip_number}
                    onChange={(e) => setNewHorse({ ...newHorse, microchip_number: e.target.value })}
                    className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                  />
                </div>
                {hasFeature('family_plan') && (
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Family Member</label>
                    <select
                      value={newHorse.family_member_id}
                      onChange={(e) => setNewHorse({ ...newHorse, family_member_id: e.target.value })}
                      className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                    >
                      <option value="">Select family member</option>
                      <option value="self">Myself (Primary Account)</option>
                      {familyMembers.map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.name} {member.relationship ? `(${member.relationship})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    placeholder="Additional notes about the horse..."
                    value={newHorse.notes}
                    onChange={(e) => setNewHorse({ ...newHorse, notes: e.target.value })}
                    className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                    rows="3"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={addHorse}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deletingHorse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold">Confirm Delete</h3>
            </div>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete <span className="font-semibold">{deletingHorse.name}</span>? This action cannot be undone and will remove all associated records (vaccinations, medical history, breeding records, etc.).
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingHorse(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (deletingHorse) {
                    deleteHorse(deletingHorse.id);
                  }
                  setShowDeleteConfirm(false);
                  setDeletingHorse(null);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
